import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// eslint-disable-next-line no-restricted-imports
import { Button, Checkbox, Form, Loader, Modal } from 'semantic-ui-react';

import { addError } from '../../helpers/errors';
import { saveNetwork } from '../networksAPI';

import { InlineError, ShowErrors } from '../../Components/Messages';
import GeoDataEditForm from './GeoDataEditForm';

export default function NetworkDetails({ network, onClose, onSave, open }) {
  const { t } = useTranslation();

  const [networkData, setNetworkData] = useState({
    id: '',
    name: '',
    logoUrl: '',
    active: true,
  });
  const [geoData, setGeoData] = useState(network?.geoData ?? []);

  const [errors, setErrors] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (network) {
      setNetworkData({ ...network });
      setGeoData(network?.geoData ?? []);
    }
  }, [network]);

  const editTitle = t('network.editNetwork');
  const addTitle = t('network.addNetwork');

  const onChange = useCallback((_, { name, value, checked }) => {
    setNetworkData((prev) => ({
      ...prev,
      [name]: value !== undefined ? value : checked,
    }));
  }, []);

  if (loading) return <Loader active />;

  return (
    <Modal closeIcon onClose={onCancel} open={open} closeOnDimmerClick={false} closeOnEscape={false}>
      <Modal.Header content={network?.id ? `${editTitle}: ${networkData?.name}` : addTitle} />

      <Modal.Content>
        {errors && <ShowErrors errors={errors} />}
        {renderInputForm()}
      </Modal.Content>
    </Modal>
  );

  function renderInputForm() {
    return (
      <Form onSubmit={onSubmit} noValidate loading={loading}>
        <Form.Field error={!!errors?.id}>
          <Form.Input
            disabled={!!network?.id}
            required
            type="text"
            id="id"
            name="id"
            value={networkData?.id}
            onChange={onChange}
            label={t('network.networkID')}
            placeholder={t('network.networkID')}
          />
          {errors?.id && <InlineError text={errors.id} />}
        </Form.Field>

        <Form.Field error={!!errors?.name}>
          <Form.Input
            required
            type="text"
            id="name"
            name="name"
            value={networkData?.name}
            onChange={onChange}
            label={t('network.networkName')}
            placeholder={t('network.networkName')}
          />
          {errors?.name && <InlineError text={errors.name} />}
        </Form.Field>

        <Form.Field error={!!errors?.active}>
          <label>{t('network.networkActive')}</label>
          <Form.Radio
            toggle
            id="active"
            name="active"
            checked={networkData?.active}
            onChange={onChange}
            control={Checkbox}
          />
          {errors?.active && <InlineError text={errors.active} />}
        </Form.Field>

        <GeoDataEditForm geoData={geoData} onChange={onGeoDataChange} onDelete={onGeoDataDelete} onAdd={onGeoDataAdd} />

        <Form.Group widths="equal">
          <Form.Field>
            <Button primary type="submit" loading={loading} content={t('networks.form.submitAction')} />
            <Button basic loading={loading} content={t('common.cancel')} onClick={onCancel} />
          </Form.Field>
        </Form.Group>
      </Form>
    );
  }
  function onGeoDataChange(index, value) {
    setGeoData(geoData.map((data, i) => (index === i ? value : data)));
  }
  function onGeoDataDelete(index) {
    setGeoData(geoData.filter((data, i) => index !== i));
  }
  function onGeoDataAdd() {
    setGeoData([...geoData, '']);
  }

  function onCancel() {
    setErrors(null);
    onClose();
  }

  async function onSubmit(e) {
    e.preventDefault();

    try {
      setLoading(true);

      const errors = validate(t);
      if (errors) {
        setLoading(false);
        setErrors(errors);
      }
      networkData.geoData = geoData.filter((geo) => geo.length > 0);
      await saveNetwork(networkData, network?.id);
      onSave();
    } catch (error) {
      setErrors(addError(errors, 'exception', error.message));
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }

  function validate(t) {
    let errors = null;

    if (!networkData.id) errors = addError(errors, 'id', t('networks.form.idRequiredError'));

    if (!networkData.name) errors = addError(errors, 'name', t('networks.form.nameRequiredError'));

    return errors;
  }
}
